import { PresentedTokenSchema } from '@zevik/schemas';

/**
 * The token an emailed link carries, read off the page's own query string
 * (docs/DECISIONS.md ZD-118 §1).
 *
 * ## What this may distinguish, and what it may not
 *
 * The API answers **one** message for a token that is unknown, expired,
 * consumed, superseded, of the other purpose, or issued to a person since
 * disabled — *this link is not valid; ask for a new one* — because which of
 * those a link hit is whether the token was ever real, and that is not the
 * request's to learn (ZD-117 §7). A page that inferred *expired* from *used*
 * would be telling an attacker what the API declined to.
 *
 * What a page may say is a claim about the **link's shape**, checked here
 * before any request is made and revealing nothing about the record: the
 * address has no token at all (somebody typed the path), or it carries one
 * that is not the shape a token has (the link was cut short, or altered in
 * transit — a mail client wrapping a long line is the usual way). Those are
 * two different things to tell a person, and neither is *the token was
 * refused*, which only the API can say.
 *
 * The token is read and returned; it is never logged, stored or put back into
 * a URL. The browser already holds it in the address bar, which is the one
 * place the contract requires it to be.
 */
export type LinkToken =
  { kind: 'present'; token: string } | { kind: 'absent' } | { kind: 'malformed' };

export function tokenFromLink(search: string): LinkToken {
  const raw = new URLSearchParams(search).get('token');
  if (raw === null || raw.length === 0) return { kind: 'absent' };
  const parsed = PresentedTokenSchema.safeParse(raw);
  return parsed.success ? { kind: 'present', token: parsed.data } : { kind: 'malformed' };
}

/** What a person is told about a link this page will not send. */
export const LINK_PROBLEMS: Readonly<Record<'absent' | 'malformed', string>> = {
  absent:
    'This page is reached from the link in your email, which carries a code this address ' +
    'does not. Open the link from the email itself.',
  malformed:
    'This link is incomplete or has been altered — usually a mail client wrapping a long ' +
    'address. Copy the whole link from the email and open it in one piece.',
};
